import React from 'react';
import { PaginationInfo } from '../../types';

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  className = ''
}) => {
  const {
    page,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    totalCount,
    limit
  } = pagination;

  // Calculate page range to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if we have few pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalCount);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 ${className}`}>
      {/* Results info */}
      <div className="text-sm text-ink-muted">
        Showing <span className="font-medium text-ink">{startItem}</span> to{' '}
        <span className="font-medium text-ink">{endItem}</span> of{' '}
        <span className="font-medium text-ink">{totalCount}</span> results
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-2">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage}
          aria-label="Previous page"
          className={`min-w-[26px] h-[26px] flex items-center justify-center text-sm font-medium rounded-md transition-colors ${
            hasPreviousPage
              ? 'text-ink hover:bg-ghost-bg'
              : 'text-ink-muted cursor-not-allowed opacity-40'
          }`}
        >
          &lsaquo;
        </button>

        {/* Page numbers */}
        <div className="flex space-x-1">
          {getPageNumbers().map((pageNum, index) => (
            <React.Fragment key={index}>
              {pageNum === '...' ? (
                <span className="px-1.5 text-sm text-ink-muted">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(pageNum as number)}
                  className={`min-w-[26px] h-[26px] px-1.5 flex items-center justify-center text-sm font-medium rounded-md transition-colors ${
                    page === pageNum
                      ? 'bg-accent text-sidebar font-bold'
                      : 'text-ink hover:bg-ghost-bg'
                  }`}
                >
                  {pageNum}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage}
          aria-label="Next page"
          className={`min-w-[26px] h-[26px] flex items-center justify-center text-sm font-medium rounded-md transition-colors ${
            hasNextPage
              ? 'text-ink hover:bg-ghost-bg'
              : 'text-ink-muted cursor-not-allowed opacity-40'
          }`}
        >
          &rsaquo;
        </button>
      </div>
    </div>
  );
};

export default Pagination;
